import { default_maximum_interval, default_w } from "ts-fsrs";

import { type DayBoundary, defaultDayBoundary } from "./review-day.js";
import type { ReviewMode } from "./modes.js";
import { freeDailyCards } from "./free-day.js";

/**
 * What a reader chose about their reviews, and what was fitted for them.
 *
 * One item under the reader, read at the top of every session. Two kinds of
 * fact sit here for one reason. The weights are what grade an answer and the
 * settings are what bound a session, and a scheduler needs both to answer a
 * single press.
 */

/** Which version of the algorithm graded a card. */
export const reviewScheme = "fsrs-6";

/** Which version of the algorithm graded a card. */
export type ReviewScheme = typeof reviewScheme;

/**
 * The name the published weights are recorded under.
 *
 * Every card and every review event carries the id of the weight set that
 * graded it. Until an optimiser runs for somebody, that is this. A fit writes
 * an id of its own, so a schedule can always be traced to the arithmetic behind
 * it. See docs/fuxi.md under "Fitting a reader's own parameters".
 */
export const publishedParameters = "fsrs-6-published";

/**
 * Whether a marked card makes a sound.
 *
 * Written as a pair of words rather than a boolean, the way every setting in
 * `src/user/reader-settings.ts` is. A radio group has to know which of its
 * values is on, and a form posts words.
 */
export type FeedbackSounds = "on" | "off";

/** What a reader chose about their reviews, and what was fitted for them. */
export interface ReviewConfig extends DayBoundary {
  /**
   * How much the reader is prepared to forget, from 0 to 1.
   *
   * 0.9 is FSRS's own default and means a card comes back when recall is
   * predicted to have fallen to nine in ten. Raising it shortens every interval
   * and buys retention with reviews.
   */
  readonly desiredRetention: number;

  /** The longest interval a card can be given, in days. */
  readonly maximumInterval: number;

  /** The questions this reader studies, which are the queues a session reads. */
  readonly modes: readonly ReviewMode[];

  /** How many cards may be introduced in one of their days. */
  readonly dailyNewLimit: number;

  /** How many cards may be answered in one of their days. */
  readonly dailyReviewLimit: number;

  /**
   * Whether a marked card is heard as well as seen.
   *
   * The one setting here that says nothing to the scheduler. It is a reader's
   * choice about their own session all the same, and the session is the only
   * page that reads it, so it travels on the item every session already reads
   * rather than in a store of its own. See `src/site/fuxi-sounds.ts`.
   */
  readonly feedbackSounds: FeedbackSounds;

  /** The 21 weights FSRS-6 grades an answer with. */
  readonly parameters: readonly number[];

  /** Which weight set those are, which every card it grades records. */
  readonly parametersId: string;

  /** Which version of the algorithm they belong to. */
  readonly scheme: ReviewScheme;
}

/**
 * What a reader gets before they have chosen anything.
 *
 * The published weights, FSRS's own retention and interval ceiling, and every
 * question this site draws. Anki hands a new collection the same
 * defaults, and they are a good scheduler on their own.
 *
 * The limits are deliberately generous rather than absent. A limit of zero
 * would read as a reader who had turned reviews off, and there is no such
 * state.
 *
 * **The sounds are on.** A reader who wants a quiet session turns them off on
 * the settings page, and one who never opens that page gets the feature. Off by
 * default it would be a switch nobody finds. A sound waits for an answered
 * card, and a page a reader has only opened stays silent.
 *
 * **`audio_to_hanzi` is on, and a word with no recording is left out rather
 * than asked.** The question plays the 词典's own recording of the word and
 * asks for the characters back, so it holds for a word the section has one of
 * and fails for a word it does not. `src/user/new-material.ts` gates the mode
 * per word against the recordings the last build found, so a reader's list
 * becomes askable by ear as the section is recorded rather than all at once.
 *
 * **`cloze_to_handwriting` is on, and it is the one question a reader cannot
 * answer from the keyboard.** It shows a word with one character taken out of
 * it and a square to draw that character on. It was offered without being a
 * default for one release, on the argument that a reader who had not asked for
 * it should not find their evening turned into drawing practice. Writing the
 * characters is the point of the exercise, so it is on, and the settings page
 * is where somebody who would rather type turns it off.
 *
 * A card is introduced only for a character the site publishes stroke data
 * for, which `src/user/handwriting-subjects.ts` settles the way
 * `audio_to_hanzi` is settled above.
 *
 * **`liju_cloze_to_hanzi` is on, and a word is asked this way only where the
 * reader holds a 例句 that uses it.** Saving a sentence puts its own words on
 * their list, so a reader who keeps sentences earns these cards as they go.
 * `src/user/new-material.ts` gates the mode per word against the sentences they
 * have kept, the way `audio_to_hanzi` is gated against the recordings.
 *
 * **`words_to_liju` is on, and a sentence is asked this way only where it
 * cuts into three to eight pieces.** It shows a saved 例句's words out of
 * order and asks for the sentence back, so a reader who keeps sentences earns
 * these cards as they go, the same as the cloze. `src/fuxi/sentence-pieces.ts`
 * holds the bound.
 *
 * **`cloze_to_components` is on, and a character is asked this way only where
 * `src/fuxi/hanzi-assemblies.json` has a row for it.** It shows the same word
 * with a character taken out that a handwriting card shows, and asks for the
 * character's parts in order instead of its strokes, which is a few taps. A
 * reader's configuration names its modes, so a reader who saved settings
 * before this mode existed turns it on there.
 *
 * `hanzi_to_english` is the one still out. It wants the English back, which is
 * marked against a list of acceptable answers rather than against one string,
 * and that list is a piece of work of its own. See docs/fuxi.md.
 */
export const defaultReviewConfig: ReviewConfig = {
  ...defaultDayBoundary,
  desiredRetention: 0.9,
  maximumInterval: default_maximum_interval,
  modes: [
    "hanzi_to_pinyin",
    "english_to_hanzi",
    "audio_to_hanzi",
    "cloze_to_handwriting",
    "liju_cloze_to_hanzi",
    "words_to_liju",
    "cloze_to_components",
  ],
  dailyNewLimit: 20,
  dailyReviewLimit: 200,
  feedbackSounds: "on",
  parameters: default_w,
  parametersId: publishedParameters,
  scheme: reviewScheme,
};

export { freeDailyCards, freeDailyExercises } from "./free-day.js";

/**
 * How far the count of cards waiting looks, for a free reader's finished day.
 *
 * A bounded read of the due queue, the way 回顾 bounds its own. Past it the
 * panel says "100 or more" rather than a number that grows with the deck.
 */
export const freeWaitingRead = 100;

/**
 * The configuration a free reader's sitting runs under.
 *
 * Their own, with the review limit held to {@link freeDailyCards}. The new
 * limit needs no holding of its own, since a sitting seats no more cards than
 * the review limit leaves and a first meeting is one of them.
 * @param config - What the reader chose.
 * @returns The same, or a copy with the review limit held down.
 */
export function freeReviewConfig(config: ReviewConfig): ReviewConfig {
  return config.dailyReviewLimit <= freeDailyCards
    ? config
    : { ...config, dailyReviewLimit: freeDailyCards };
}
